import { COLOR_SCHEME_QUERY, THEME_ATTRIBUTE, THEME_STORAGE_KEY } from '@config/theme';
import { useAppStore } from '@stores/index';
import type { ResolvedTheme, ThemePreference } from '@typings/theme';

/** Painted behind everything before CSS loads; must match _themes.scss. */
const THEME_BACKGROUND: Readonly<Record<ResolvedTheme, string>> = {
  light: '#f3f5fa',
  dark: '#06080d',
};

/**
 * Present on <html> only while a theme change is cross-fading (see
 * base/_motion.scss). The same fade runs with or without reduced motion:
 * it has no movement in it.
 */
const FADE_CLASS = 'is-theme-fading';

const readStoredPreference = (): ThemePreference => {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    /* Storage can throw in private windows or with site data blocked. */
    return 'system';
  }
};

const writeStoredPreference = (preference: ThemePreference): void => {
  try {
    if (preference === 'system') localStorage.removeItem(THEME_STORAGE_KEY);
    else localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    /* Not persisting is acceptable; the choice still holds this session. */
  }
};

/**
 * Owns the theme: resolves preference against the OS, writes the result to
 * <html>, persists the choice and animates the switch.
 *
 * The store holds the state so React can render the toggle; this class does
 * the side effects, because they must happen in one place regardless of
 * which component asked for the change.
 */
class ThemeManager {
  private mediaQuery: MediaQueryList | null = null;
  private unsubscribe: (() => void) | null = null;

  private readonly handleSystemChange = (): void => {
    if (useAppStore.getState().themePreference === 'system') this.resolve();
  };

  init(): void {
    if (this.mediaQuery) return;

    this.mediaQuery = window.matchMedia(COLOR_SCHEME_QUERY);
    this.mediaQuery.addEventListener('change', this.handleSystemChange);

    const store = useAppStore.getState();
    store.setThemePreference(readStoredPreference());
    this.resolve();

    this.unsubscribe = useAppStore.subscribe(
      (state) => state.themePreference,
      (preference) => {
        writeStoredPreference(preference);
        this.resolve();
      },
    );
  }

  /**
   * Switches theme, cross-fading the whole page from the old theme to the
   * new one with a view transition.
   *
   * A circle growing from the toggle was tried first. Starting in a corner,
   * it covered the screen in a fraction of its duration and read as a
   * sudden change, which is the opposite of what a theme switch should feel
   * like. Without view transition support the switch is instant.
   */
  setPreference(preference: ThemePreference): void {
    const store = useAppStore.getState();
    const apply = (): void => {
      store.setThemePreference(preference);
    };

    if (!('startViewTransition' in document)) {
      apply();
      return;
    }

    const root = document.documentElement;
    root.classList.add(FADE_CLASS);

    const transition = document.startViewTransition(apply);
    void transition.finished.finally(() => {
      root.classList.remove(FADE_CLASS);
    });
  }

  dispose(): void {
    this.mediaQuery?.removeEventListener('change', this.handleSystemChange);
    this.mediaQuery = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
  }

  private resolve(): void {
    const { themePreference, setResolvedTheme } = useAppStore.getState();
    const theme: ResolvedTheme =
      themePreference === 'system'
        ? this.mediaQuery?.matches
          ? 'light'
          : 'dark'
        : themePreference;

    document.documentElement.setAttribute(THEME_ATTRIBUTE, theme);

    /* The two media-scoped theme-color tags in index.html are wrong once the
       visitor overrides the OS, so they are pinned to the resolved theme. */
    for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
      meta.content = THEME_BACKGROUND[theme];
      meta.removeAttribute('media');
    }

    setResolvedTheme(theme);
  }
}

export const themeManager = new ThemeManager();

if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    themeManager.dispose();
  });
}
